import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import './LicenseBlocker.css';

const LicenseBlocker = ({ user, onUnblock }) => {
  const { t } = useTranslation();
  const [licenseStatus, setLicenseStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUpgrade, setShowUpgrade] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  // Fonction temporaire pour gérer les demandes d'upgrade
  const handleUpgradeRequest = () => {
    toast.info(t('licenseBlocker.upgradeRequest.title') + '\n\n' +
          t('licenseBlocker.upgradeRequest.paymentSoon') + '\n' +
          t('licenseBlocker.upgradeRequest.contactUs') + '\n' +
          '   • Email: nodemented.contact@gmail.com\n' +
          '   • ' + t('licenseBlocker.upgradeRequest.subject') + ' - ' + (user?.email || t('licenseBlocker.upgradeRequest.user')) + '\n\n' +
          t('licenseBlocker.upgradeRequest.allOptions'));
  };

  useEffect(() => {
    if (user) {
      checkLicenseStatus();
    }
  }, [user]);

  const checkLicenseStatus = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/license/my-license`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setLicenseStatus(data);
        
        // Si la licence est valide, débloquer
        if (data.hasLicense && !data.license.isExpired) {
          if (onUnblock) onUnblock();
        }
      }
    } catch (error) {
      console.error('Erreur vérification licence:', error);
    } finally {
      setLoading(false);
    }
  };

  const startFreeTrial = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/license/start-trial`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        toast.success(t('licenseBlocker.startTrial.success'));
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        const error = await response.json();
        toast.error(`❌ ${error.error || t('licenseBlocker.startTrial.error')}`);
      }
    } catch (error) {
      console.error('Erreur activation trial:', error);
      toast.error(t('licenseBlocker.startTrial.technicalError'));
    }
  };

  const shouldBlock = () => {
    if (!licenseStatus) return true;
    
    // Bloquer si pas de licence ou licence expirée
    return !licenseStatus.hasLicense || licenseStatus.license?.isExpired;
  };

  if (loading) {
    return (
      <div className="license-blocker loading">
        <div className="loading-spinner"></div>
        <p>{t('licenseBlocker.loading')}</p>
      </div>
    );
  }

  if (!shouldBlock()) {
    return null; // Pas de blocage, laisser passer
  }

  const isExpired = licenseStatus?.hasLicense && licenseStatus?.license?.isExpired;
  const noLicense = !licenseStatus?.hasLicense;

  return (
    <div className="license-blocker">
      <div className="blocker-overlay"></div>
      <div className="blocker-content">
        <div className="blocker-header">
          <div className="blocker-icon">
            {isExpired ? '⏰' : '🎫'}
          </div>
          <h1>
            {isExpired ? t('licenseBlocker.expired.title') : t('licenseBlocker.noLicense.title')}
          </h1>
          <p>
            {isExpired 
              ? t('licenseBlocker.expired.description')
              : t('licenseBlocker.noLicense.description')
            }
          </p>
        </div>

        <div className="blocker-features">
          <h3>🚀 {t('licenseBlocker.features.title')}</h3>
          <div className="features-grid">
            <div className="feature-item">
              <span className="feature-icon">🕸️</span>
              <span>{t('licenseBlocker.features.unlimitedGraphs')}</span>
            </div>
            <div className="feature-item">
              <span className="feature-icon">🤖</span>
              <span>{t('licenseBlocker.features.advancedAI')}</span>
            </div>
            <div className="feature-item">
              <span className="feature-icon">📊</span>
              <span>{t('licenseBlocker.features.completeDashboard')}</span>
            </div>
            <div className="feature-item">
              <span className="feature-icon">👥</span>
              <span>{t('licenseBlocker.features.teamCollaboration')}</span>
            </div>
            <div className="feature-item">
              <span className="feature-icon">📈</span>
              <span>{t('licenseBlocker.features.detailedAnalytics')}</span>
            </div>
            <div className="feature-item">
              <span className="feature-icon">🔒</span>
              <span>{t('licenseBlocker.features.enhancedSecurity')}</span>
            </div>
          </div>
        </div>

        <div className="blocker-actions">
          {noLicense && (
            <button 
              className="btn-primary btn-large"
              onClick={startFreeTrial}
            >
              🎉 {t('licenseBlocker.actions.startTrial')}
            </button>
          )}
          
          <button 
            className="btn-upgrade btn-large"
            onClick={handleUpgradeRequest}
          >
            💎 {t('licenseBlocker.actions.viewPlans')}
          </button>
          
          <button 
            className="btn-secondary"
            onClick={checkLicenseStatus}
          >
            🔄 {t('licenseBlocker.actions.checkLicense')}
          </button>
        </div>

        {isExpired && licenseStatus?.license && (
          <div className="expired-info">
            <p>
              <strong>{t('licenseBlocker.expired.expiredOn')}:</strong> {' '}
              {new Date(licenseStatus.license.endDate).toLocaleDateString()}
            </p>
            <p>
              <strong>{t('licenseBlocker.expired.previousPlan')}:</strong> {licenseStatus.license.plan}
            </p>
          </div>
        )}
      </div>

      {/* Modal upgrade */}
      {showUpgrade && (
        <div className="upgrade-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h2>💎 {t('licenseBlocker.upgrade.chooseYourPlan')}</h2>
              <button 
                className="modal-close"
                onClick={() => setShowUpgrade(false)}
              >
                ✕
              </button>
            </div>
            
            <div className="plans-grid">
              <div className="plan-card starter">
                <h3>{t('licenseBlocker.plans.starter.name')}</h3>
                <div className="plan-price">29€<span>/mois</span></div>
                <ul>
                  <li>✅ {t('licenseBlocker.plans.starter.nodes')}</li>
                  <li>✅ {t('licenseBlocker.plans.starter.apiCalls')}</li>
                  <li>✅ {t('licenseBlocker.plans.starter.storage')}</li>
                  <li>✅ {t('licenseBlocker.plans.starter.users')}</li>
                  <li>✅ {t('licenseBlocker.plans.starter.support')}</li>
                </ul>
                <button className="btn-plan">{t('licenseBlocker.plans.starter.choose')}</button>
              </div>
              
              <div className="plan-card professional recommended">
                <div className="plan-badge">{t('licenseBlocker.plans.professional.badge')}</div>
                <h3>{t('licenseBlocker.plans.professional.name')}</h3>
                <div className="plan-price">99€<span>/mois</span></div>
                <ul>
                  <li>✅ {t('licenseBlocker.plans.professional.nodes')}</li>
                  <li>✅ {t('licenseBlocker.plans.professional.apiCalls')}</li>
                  <li>✅ {t('licenseBlocker.plans.professional.storage')}</li>
                  <li>✅ {t('licenseBlocker.plans.professional.users')}</li>
                  <li>✅ {t('licenseBlocker.plans.professional.support')}</li>
                  <li>✅ {t('licenseBlocker.plans.professional.advancedAPI')}</li>
                </ul>
                <button className="btn-plan primary">{t('licenseBlocker.plans.professional.choose')}</button>
              </div>
              
              <div className="plan-card enterprise">
                <h3>{t('licenseBlocker.plans.enterprise.name')}</h3>
                <div className="plan-price">299€<span>/mois</span></div>
                <ul>
                  <li>✅ {t('licenseBlocker.plans.enterprise.nodes')}</li>
                  <li>✅ {t('licenseBlocker.plans.enterprise.apiCalls')}</li>
                  <li>✅ {t('licenseBlocker.plans.enterprise.storage')}</li>
                  <li>✅ {t('licenseBlocker.plans.enterprise.users')}</li>
                  <li>✅ {t('licenseBlocker.plans.enterprise.support')}</li>
                  <li>✅ {t('licenseBlocker.plans.enterprise.sla')}</li>
                </ul>
                <button className="btn-plan">{t('licenseBlocker.plans.enterprise.choose')}</button>
              </div>
            </div>
            
            <div className="modal-footer">
              <p>💳 {t('licenseBlocker.plans.footer')}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LicenseBlocker;
